import React, { useCallback, useEffect, useState } from "react";

import * as strings from "../../../../strings";
import * as ArrayUtils from "../../../../lib/util/ArrayUtils";

import Text from "../../../common/Text/Text";
import Checkbox from "../../../form/Checkbox/Checkbox";
import { Translatable } from "../../../../lib/util/Translatable";
import {
	AdvancedOption,
	flattenAdvancedOptions,
} from "../AdvancedSearchFilter";

export type AdvancedFilterItemToggleCallback = (
	id: string,
	value: string,
	checked: boolean,
	type?: "includeChildren" | "excludeChildren",
) => void;

export interface AdvancedFilterItemProps {
	onToggle?: AdvancedFilterItemToggleCallback;
	onChange?: (id: string, selectedOptions: string[]) => void;
	option: AdvancedOption;
	options: AdvancedOption[];
	id: string;
	checker: (item: string) => boolean;
	property: string;
	selectedOptions: string[] | null;
	style?: object;
	hasChildren?: boolean;
	indeterminateFunc?: null | ((item: string) => boolean);
	withoutSubOptsTitle?: Translatable | string;
	subFilter?: string;
}

interface AdvancedFilterItemChildToggleProps {
	value: string;
	onClick: (value: string, checked: boolean) => void;
	checked: boolean;
	withoutSubOptsTitle?: Translatable | string;
}

const AdvancedFilterItemChildToggle: React.FC<AdvancedFilterItemChildToggleProps> =
	(props) => {
		const handleClick = useCallback(() => {
			props.onClick(props.value, !props.checked);
		}, [props]);

		return (
			<button
				className={"AdvancedFilterItem__child-toggle"}
				onClick={handleClick}
			>
				<Text>
					{props.withoutSubOptsTitle ||
						strings.ADVANCED_SEARCH_WITHOUT_SUB_OPTS}
				</Text>
			</button>
		);
	};

const AdvancedFilterItem: React.FC<AdvancedFilterItemProps> = (props) => {
	const [hovering, setHovering] = useState(false);
	const [checked, setChecked] = useState(false);
	const [indeterminate, setIndeterminate] = useState(false);
	const {
		selectedOptions,
		onChange,
		onToggle,
		checker,
		indeterminateFunc,
		option,
		id,
		subFilter,
		options,
	} = props;

	const handleToggle = useCallback(
		(
			value: string,
			checked: boolean,
			type?: "includeChildren" | "excludeChildren",
		) => {
			let newSelectedOptions = selectedOptions
				? [...selectedOptions]
				: options.map((option) => option.value);

			if (type === "excludeChildren") {
				newSelectedOptions = checked
					? ArrayUtils.uniquePush(newSelectedOptions, value)
					: newSelectedOptions.filter((item) => item !== value);
			} else {
				const flatChildren = option.children
					? flattenAdvancedOptions(option.children, subFilter)
					: [];
				flatChildren.unshift(option);
				if (checked) {
					for (const option of flatChildren) {
						ArrayUtils.uniquePush(newSelectedOptions, option.value);
					}
				} else {
					newSelectedOptions = newSelectedOptions.filter(
						(id) => !flatChildren.some((child) => child.value === id),
					);
				}
			}
			if (onToggle) {
				onToggle(id, value, checked, type);
			}
			if (onChange) {
				onChange(id, newSelectedOptions);
			}
		},
		[id, onChange, onToggle, option, options, selectedOptions, subFilter],
	);

	const handleChange = useCallback(
		(evt: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
			handleToggle(evt.currentTarget.value, checked);
		},
		[handleToggle],
	);

	const handleWithoutChildrenClick = useCallback(
		(value: string, checked: boolean) => {
			handleToggle(value, checked, "excludeChildren");
		},
		[handleToggle],
	);

	const handleHover = useCallback(
		(evt: React.MouseEvent) => {
			setHovering(evt.type === "mouseenter");
		},
		[setHovering],
	);

	useEffect(() => {
		setChecked(checker(option.value));

		if (indeterminateFunc) {
			setIndeterminate(indeterminateFunc(option.value));
		}
	}, [props, checker, indeterminateFunc, option.value]);

	return (
		<div
			style={props.style}
			onMouseEnter={handleHover}
			onMouseLeave={handleHover}
		>
			<Checkbox
				onChange={handleChange}
				value={option.value}
				checked={checked}
				size={"small"}
				indeterminate={indeterminate}
			/>
			<Text tag={"span"}>{option.title}</Text>
			{props.hasChildren && hovering && (
				<AdvancedFilterItemChildToggle
					onClick={handleWithoutChildrenClick}
					value={option.value}
					checked={checked}
					withoutSubOptsTitle={props.withoutSubOptsTitle}
				/>
			)}
		</div>
	);
};

export default AdvancedFilterItem;
